import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Wallet, Ticket, Handshake, Image as ImageIcon,
  Users, Settings, LifeBuoy, LogOut, Building2, TrendingUp, Receipt,
} from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { Button } from "./ui/button";

const groups = [
  {
    label: "Event Management",
    items: [
      { to: "/", icon: LayoutDashboard, label: "Dashboard", tid: "nav-dashboard" },
      { to: "/events", icon: CalendarDays, label: "Events", tid: "nav-events" },
    ],
  },
  {
    label: "Accounting",
    items: [
      { to: "/accounting", icon: Wallet, label: "Overview", tid: "nav-accounting" },
      { to: "/accounting/budgeting", icon: Receipt, label: "Budgeting Forecast", tid: "nav-budgeting" },
      { to: "/accounting/costs", icon: Receipt, label: "Actual Cost Ledger", tid: "nav-costs" },
      { to: "/accounting/sales", icon: TrendingUp, label: "Sales Analytics", tid: "nav-sales" },
      { to: "/accounting/projections", icon: TrendingUp, label: "Revenue Projections", tid: "nav-projections" },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/admin/users", icon: Users, label: "User Management", tid: "nav-users", adminOnly: true },
      { to: "/admin/settings", icon: Settings, label: "System Settings", tid: "nav-settings", adminOnly: true },
      { to: "/support", icon: LifeBuoy, label: "Support & Docs", tid: "nav-support" },
    ],
  },
];

export default function Layout({ children }) {
  const { user, logout, currency, setCurrency, fxRate } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex bg-[var(--bg)]">
      {/* Sidebar */}
      <aside className="w-64 border-r border-[var(--border-1)] bg-white flex flex-col" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-[var(--border-1)]">
          <div className="flex items-center gap-3">
            <img
              src="/assets/logo.png"
              alt="Events Management Team"
              className="w-11 h-11 object-cover rounded-full ring-1 ring-[var(--border-1)] bg-black"
              data-testid="sidebar-logo"
            />
            <div>
              <div className="font-serif text-lg leading-none">Event Team</div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--text-2)] mt-1">We create · You celebrate</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto sidebar-scroll py-4">
          {groups.map((g) => (
            <div key={g.label} className="mb-4">
              <div className="px-6 mb-2 text-[10px] uppercase tracking-widest text-[var(--text-2)]">
                {g.label}
              </div>
              {g.items
                .filter((i) => !i.adminOnly || user?.role === "admin")
                .map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.to === "/"}
                    data-testid={it.tid}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-6 py-2 text-sm transition-colors duration-150 border-l-2 ${
                        isActive
                          ? "border-[var(--accent)] bg-[var(--bg)] text-[var(--text-1)] font-medium"
                          : "border-transparent text-[var(--text-2)] hover:bg-[var(--bg)] hover:text-[var(--text-1)]"
                      }`
                    }
                  >
                    <it.icon className="w-4 h-4" />
                    <span>{it.label}</span>
                  </NavLink>
                ))}
            </div>
          ))}
        </nav>

        <div className="border-t border-[var(--border-1)] px-6 py-4 text-[11px] text-[var(--text-2)] leading-relaxed">
          <div className="font-serif text-[13px] text-[var(--text-1)] mb-1">Contact</div>
          Beirut, Lebanon<br />
          info@eventsmanagement.team<br />
          +961 81 998 100
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-[var(--border-1)] bg-white px-6 flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-[var(--text-2)]">
            Event Operations · Accounting Console
          </div>
          <div className="flex items-center gap-4">
            <div className="mono text-xs text-[var(--text-2)]">
              1 USD = {fxRate.toFixed(4)} EUR
            </div>
            <div className="inline-flex border border-[var(--border-1)]">
              {["USD", "EUR"].map((c) => (
                <button
                  key={c}
                  data-testid={`currency-${c.toLowerCase()}`}
                  onClick={() => setCurrency(c)}
                  className={`px-3 py-1 text-xs mono ${
                    currency === c
                      ? "bg-[var(--primary)] text-white"
                      : "bg-white text-[var(--text-2)] hover:bg-[var(--bg)]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="text-xs text-[var(--text-1)]">
              <div className="font-medium">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--text-2)]">
                {user?.role}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              data-testid="logout-btn"
              onClick={async () => {
                await logout();
                nav("/login");
              }}
              className="rounded-none"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-8">{children}</main>

        <footer className="border-t border-[var(--border-1)] bg-white px-6 py-3 text-[11px] text-[var(--text-2)] flex justify-between">
          <div>© {new Date().getFullYear()} Event Team · All rights reserved.</div>
          <div>Beirut, Lebanon · info@eventsmanagement.team · +961 81 998 100</div>
        </footer>
      </div>
    </div>
  );
}
